import { useCallback, useEffect, useState } from 'react';

declare global {
	interface Window {
		grecaptcha: {
			enterprise: {
				ready: (cb: () => void) => void;
				execute: (siteKey: string, options: { action: string }) => Promise<string>;
			};
		};
	}
}

export type useRecaptchaParams = {
	recaptchaKey: string;
	action?: string;
};

export function useGoogleRecaptcha({ recaptchaKey, action = '' }: useRecaptchaParams) {
	const [ready, setReady] = useState(false);

	// load the google recaptcha script into the head tag
	// if the siteKey is not provided, the recaptcha will not be loaded but
	// will be marked as ready
	useEffect(() => {
		// do nothing if the siteKey is not provided
		if (!recaptchaKey) {
			setReady(true);
			return;
		}

		const script = document.createElement('script');
		try {
			// inject the google recaptcha script into the head tag in a useEffect hook
			const scriptURL = new URL('https://www.google.com/recaptcha/enterprise.js');
			scriptURL.searchParams.set('render', recaptchaKey || '');

			script.onload = () => {
				console.debug('recaptcha loaded');
				window.grecaptcha.enterprise.ready(() => {
					setReady(true);
				});
			};
			script.onerror = () => {
				console.error('Failed to load recaptcha');
			};
			script.src = scriptURL.toString();
			script.async = true;
			script.defer = true;

			document.head.appendChild(script);
		} catch (e) {
			console.error('Failed to load recaptcha', e);
		}

		return () => {
			setReady(false);
			console.debug('recaptcha unloaded');
			try {
				document.head.removeChild(script);
			} catch (e) {}
		};
	}, [recaptchaKey]);

	// generate a recaptcha token when a user action is executed
	// return an empty string if the siteKey is not provided
	const execute = useCallback(async () => {
		if (!recaptchaKey) {
			return '';
		}

		if (!ready) {
			console.log('recaptcha was not loaded properly, skipping check');
			return '';
		}

		try {
			const res = await window.grecaptcha.enterprise.execute(recaptchaKey, {
				action,
			});

			return res;
		} catch (e) {
			console.error('Failed to execute recaptcha, skipping', e);
			return '';
		}
	}, [recaptchaKey, action, ready]);

	return { ready, execute };
}
